/**
 * Canonical links into draftedge.com — the single front door for signup,
 * billing, and account management.
 *
 * Fantasy is a CONSUMER of draftedge membership: it never registers users,
 * sells subscriptions, or resets passwords. Every such CTA in this SPA points
 * here. Override the base per-environment with VITE_DRAFTEDGE_URL (e.g. a
 * staging host); defaults to production.
 */
const BASE = (
  (import.meta.env.VITE_DRAFTEDGE_URL as string | undefined) ?? 'https://draftedge.com'
).replace(/\/+$/, '');

export const DRAFTEDGE = {
  base: BASE,
  /**
   * Create a DraftEdge account. The fantasy app is a premium product, so
   * every in-app "Create account / Create one" preselects the NFL Draft Kit
   * plan — draftedge's signup then flows straight into Stripe checkout
   * (?plan=draft_kit) instead of dead-ending on a free account.
   */
  register: `${BASE}/register/?plan=draft_kit`,
  /** Sign in on draftedge (e.g. password recovery landings). */
  login: `${BASE}/login`,
  /** Plans / pricing — where users subscribe or upgrade. */
  membership: `${BASE}/membership-info/`,
  /** Manage / cancel an existing subscription. */
  subscription: `${BASE}/account/subscription/`,
  /** Password reset. */
  forgotPassword: `${BASE}/forgot-password`,
} as const;

/** Destinations the reverse-SSO bridge can drop you on (server-whitelisted). */
export type DraftedgeSsoDest = 'dashboard' | 'subscription' | 'account';

/**
 * Build a reverse-SSO bridge URL. For an authenticated fantasy user this hands
 * them to draftedge.com ALREADY SIGNED IN (no second login), landing on `to`.
 * The bridge (GET /v2/auth/sso/bridge) reads the SPA's bearer token client-side,
 * mints a one-time HMAC handoff, and redirects. If the user isn't signed in on
 * fantasy (no token), it falls back to `fallback` (a plain draftedge URL) when
 * given, otherwise the fantasy login.
 *
 * Use this ONLY for links a signed-in user follows to manage their DraftEdge
 * account/billing — NOT for logged-out signup/pricing entry points.
 */
export function draftedgeSso(to: DraftedgeSsoDest, fallback?: string): string {
  let url = `/v2/auth/sso/bridge?to=${encodeURIComponent(to)}`;
  if (fallback) {
    url += `&fallback=${encodeURIComponent(fallback)}`;
  }
  return url;
}
